/**
 * Controller for the "Join workspace" wizard: resolves the current step
 * from the URL (/join/:orgId/:stepId), renders the layout + step header,
 * and wires Back navigation between steps.
 */
import { useCallback, useEffect, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { SelfServiceLayout } from '@/lib/self-service/components/SelfServiceLayout';

import { useJoinOnboardingStatus } from '../api';
import {
  findJoinStepIndex,
  FIRST_JOIN_STEP_ID,
  JOIN_STEPS,
  joinStepPath,
} from '../constants';
import { JoinFlowProvider } from '../flowState';
import { JOIN_STEP_COMPONENTS } from './steps/registry';

/** Shown until the org name resolves — never render a bare "Join ". */
const FALLBACK_FLOW_TITLE = 'Join workspace';

export function JoinWorkspaceFlow() {
  const { orgId, stepId } = useParams<{ orgId: string; stepId: string }>();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const status = useJoinOnboardingStatus(orgId ?? '');

  const stepIndex = findJoinStepIndex(stepId);

  // The invitee is joining a workspace that already has a name, so say which one.
  // `org_name` arrives with the status fetch; fall back until it does.
  const orgName = status.data?.org_name;
  const flowTitle = orgName ? `Join ${orgName}` : FALLBACK_FLOW_TITLE;

  // Stable step-path callback (hooks must precede early returns).
  const stepPath = useCallback(
    (id: string) => joinStepPath(orgId ?? '', id),
    [orgId]
  );

  useEffect(() => {
    headingRef.current?.focus();
  }, [stepId]);

  if (!orgId) {
    return <Navigate to='/organizations' replace />;
  }

  // Unknown step slug → redirect to the first step.
  if (stepIndex === -1) {
    return <Navigate to={joinStepPath(orgId, FIRST_JOIN_STEP_ID)} replace />;
  }

  const step = JOIN_STEPS[stepIndex];
  const backTo =
    stepIndex === 0
      ? '/organizations'
      : joinStepPath(orgId, JOIN_STEPS[stepIndex - 1].id);
  const StepComponent = JOIN_STEP_COMPONENTS[step.id];

  return (
    <JoinFlowProvider orgId={orgId}>
      <SelfServiceLayout
        title={flowTitle}
        leaveTo='/organizations'
        backTo={backTo}
        steps={JOIN_STEPS}
        stepPath={stepPath}
        currentStepIndex={stepIndex}
      >
        {/* Step header */}
        <div className='mb-8 flex flex-col'>
          <p
            role='status'
            aria-live='polite'
            className='text-sm font-medium text-[var(--color-text-primary)]'
          >
            Step {stepIndex + 1} of {JOIN_STEPS.length}
          </p>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className='mt-2 font-display text-4xl font-medium text-brand-600 dark:text-brand-400'
          >
            {step.title}
          </h1>
          {step.subtitle && (
            <p className='mt-3 text-base text-[var(--color-text-tertiary)]'>
              {step.subtitle}
            </p>
          )}
        </div>

        {/* Step body resolved from the registry */}
        <StepComponent />
      </SelfServiceLayout>
    </JoinFlowProvider>
  );
}
